import api from './api';

export const followUpService = {
  getAllFollowUps: async (params = {}) => {
    try {
      const response = await api.get('/follow-ups', { params });
      return response.data;
    } catch (error) {
      console.error('Error getting follow-up records:', error);
      throw error;
    }
  },

  getFollowUpById: async (id) => {
    try {
      const response = await api.get(`/follow-ups/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error getting follow-up record:', error);
      throw error;
    }
  },

  createFollowUp: async (followUpData) => {
    try {
      const response = await api.post('/follow-ups', followUpData);
      return response.data;
    } catch (error) {
      console.error('Error creating follow-up record:', error);
      throw error;
    }
  },

  updateFollowUp: async (id, followUpData) => {
    try {
      const response = await api.put(`/follow-ups/${id}`, followUpData);
      return response.data;
    } catch (error) {
      console.error('Error updating follow-up record:', error);
      throw error;
    }
  },

  deleteFollowUp: async (id) => {
    try {
      const response = await api.delete(`/follow-ups/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting follow-up record:', error);
      throw error;
    }
  },

  bulkDeleteFollowUps: async (followUpIds) => {
    try {
      const response = await api.delete('/follow-ups/bulk', {
        data: { followUpIds }
      });
      return response.data;
    } catch (error) {
      console.error('Error bulk deleting follow-up records:', error);
      throw error;
    }
  },

  getFollowUpStats: async (startDate, endDate) => {
    try {
      const response = await api.get('/follow-ups/stats', { 
        params: { startDate, endDate } 
      });
      return response.data;
    } catch (error) {
      console.error('Error getting follow-up stats:', error);
      throw error;
    }
  },

  getFollowUpsByStatus: async (status) => {
    try {
      const response = await api.get(`/follow-ups/status/${status}`);
      return response.data;
    } catch (error) {
      console.error('Error getting follow-ups by status:', error);
      throw error;
    }
  },

  getFollowUpsByPriority: async (priority) => {
    try {
      const response = await api.get(`/follow-ups/priority/${priority}`);
      return response.data;
    } catch (error) {
      console.error('Error getting follow-ups by priority:', error);
      throw error;
    }
  },

  getFollowUpsByAssignee: async (assigneeId) => {
    try {
      const response = await api.get(`/follow-ups/assignee/${assigneeId}`);
      return response.data;
    } catch (error) {
      console.error('Error getting follow-ups by assignee:', error);
      throw error;
    }
  },

  getOverdueFollowUps: async () => {
    try {
      const response = await api.get('/follow-ups/overdue');
      return response.data;
    } catch (error) {
      console.error('Error getting overdue follow-ups:', error);
      throw error;
    }
  },

  getTodayFollowUps: async () => {
    try {
      const response = await api.get('/follow-ups/today');
      return response.data;
    } catch (error) {
      console.error('Error getting today\'s follow-ups:', error);
      throw error;
    }
  },

  getFollowUpCode: async () => {
    try {
      const response = await api.get('/auto-increment/curr', {
        params: { sequenceName: 'FOLLOW-UP' }
      });
      return response.data;
    } catch (error) {
      console.error('Error getting follow-up code:', error);
      throw error;
    }
  },

  completeFollowUp: async (id, completionData) => {
    try {
      const response = await api.post(`/follow-ups/${id}/complete`, completionData);
      return response.data;
    } catch (error) {
      console.error('Error completing follow-up:', error);
      throw error;
    }
  },

  escalateFollowUp: async (id, escalationData) => {
    try {
      const response = await api.post(`/follow-ups/${id}/escalate`, escalationData);
      return response.data;
    } catch (error) {
      console.error('Error escalating follow-up:', error);
      throw error;
    }
  },

  // Follow-up validation helpers
  validateFollowUpData: (followUpData) => {
    const errors = [];
    
    if (!followUpData.subject || followUpData.subject.trim() === '') {
      errors.push('Subject is required');
    }
    
    if (!followUpData.followUpDate) {
      errors.push('Follow-up date is required');
    }
    
    if (!followUpData.assignedTo || followUpData.assignedTo.trim() === '') {
      errors.push('Assigned to is required');
    }
    
    if (!followUpData.followUpMethod) {
      errors.push('Follow-up method is required');
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  },

  // Follow-up formatting helpers
  formatFollowUpForDisplay: (followUp) => {
    return {
      ...followUp,
      formattedDate: new Date(followUp.followUpDate).toLocaleDateString(),
      formattedTime: new Date(followUp.followUpTime).toLocaleTimeString(),
      isOverdue: new Date(followUp.followUpDate) < new Date() && followUp.followUpStatus !== 'Completed',
      isToday: new Date(followUp.followUpDate).toDateString() === new Date().toDateString(),
      priorityColor: {
        'High': 'text-red-600',
        'Medium': 'text-yellow-600',
        'Low': 'text-green-600'
      }[followUp.priority] || 'text-gray-600',
      statusColor: {
        'Pending': 'text-yellow-600',
        'In Progress': 'text-blue-600',
        'Completed': 'text-green-600',
        'Overdue': 'text-red-600',
        'Cancelled': 'text-gray-600'
      }[followUp.followUpStatus] || 'text-gray-600'
    };
  },

  // Follow-up search helpers
  searchFollowUps: async (searchTerm, filters = {}) => {
    try {
      const params = {
        search: searchTerm,
        ...filters
      };
      const response = await api.get('/follow-ups', { params });
      return response.data;
    } catch (error) {
      console.error('Error searching follow-ups:', error);
      throw error;
    }
  },

  // Follow-up filtering helpers
  getFollowUpFilters: () => {
    return {
      status: ['Pending', 'In Progress', 'Completed', 'Overdue', 'Cancelled'],
      priority: ['High', 'Medium', 'Low'],
      method: ['Phone Call', 'Email', 'Video Call', 'Meeting', 'Message'],
      type: ['Task', 'Reminder', 'Follow-up', 'Action Item', 'To-do', 'Note']
    };
  },

  // Follow-up analytics helpers
  calculateFollowUpMetrics: (followUps) => {
    const totalFollowUps = followUps.length;
    const pendingFollowUps = followUps.filter(fu => fu.followUpStatus === 'Pending').length;
    const completedFollowUps = followUps.filter(fu => fu.followUpStatus === 'Completed').length;
    const overdueFollowUps = followUps.filter(fu => 
      new Date(fu.followUpDate) < new Date() && fu.followUpStatus !== 'Completed'
    ).length;
    const todayFollowUps = followUps.filter(fu => 
      new Date(fu.followUpDate).toDateString() === new Date().toDateString()
    ).length;

    return {
      totalFollowUps,
      pendingFollowUps,
      completedFollowUps,
      overdueFollowUps,
      todayFollowUps,
      completionRate: totalFollowUps > 0 ? (completedFollowUps / totalFollowUps * 100).toFixed(2) : 0,
      overdueRate: totalFollowUps > 0 ? (overdueFollowUps / totalFollowUps * 100).toFixed(2) : 0
    };
  }
};
